import {computed, makeObservable} from 'mobx';

import type {ID, Nullable} from '../../../utils/utility-types';
import type {DateTimeModel} from '../Date/DateTime.model';
import type {EditionModel} from '../Edition/Edition.model';
import type {FileModel} from '../File/File.model';
import type {ProgramModel} from '../Program/Program.model';
import type {UserModel} from '../User/User.model';
import type {CourseRestart} from './CourseRestart';

import {BaseModel} from '../Base/Base.model';

const ModelName = 'CourseModel';

export class CourseModel extends BaseModel {
  avatar: Nullable<FileModel>;
  channelIds: ID[];
  client: Nullable<UserModel>;
  clientsCount: number;
  coachIds: ID[];
  currentCycle: number;
  currentUsedOfferId: Nullable<string>;
  durationString: string;
  edition: Nullable<EditionModel>;
  endDate: Nullable<DateTimeModel>; // cycle end date
  isAccessFrozen: boolean;
  isArchived: boolean;
  isAutoRestarting: boolean;
  isEnded: boolean;
  isIndividual: boolean;
  isMeCoach: boolean;
  isOneToOne: boolean;
  isStarted: boolean;
  label: string;
  numberOfCycles: number;
  program: ProgramModel;
  programEndDate: Nullable<DateTimeModel>; // full cycle end date
  restart: Nullable<CourseModel.Restart>;
  startDate: Nullable<DateTimeModel>;
  title: string;
  updatedDate: Nullable<DateTimeModel>;
  usedOfferIds: string[];

  constructor(props: CourseModel.Props) {
    super(props);
    Object.assign(this, props);

    makeObservable(
      this,
      BaseModel.mobxAnnotationFactory<CourseModel, CourseModel.Props>(props, {
        firstChannelId: computed,
        labelOrDurationString: computed,
      }),
    );
  }

  get firstChannelId() {
    return this.channelIds[0];
  }

  get labelOrDurationString() {
    return this.label || this.durationString;
  }

  static override get modelName(): typeof ModelName {
    return ModelName;
  }

  get modelName(): typeof ModelName {
    return ModelName;
  }
}

export namespace CourseModel {
  export type Props = BaseModel.ModelToProps<
    CourseModel,
    'firstChannelId' | 'labelOrDurationString'
  >;

  export type Restart = {
    date: Nullable<DateTimeModel>;
    history: CourseRestart[];
  };

  export type Status =
    | 'archived'
    | 'canceled'
    | 'ongoing'
    | 'paused'
    | 'planned';
}
